import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Button, Modal } from 'antd';
import { useEmployeeProfiles } from '@/app/oz/_shared/hooks';
import { ModalRowsSkeleton } from '@/components/profile';
import { ROUTES } from '@/constants';

const money = (v) =>
  v === null || v === undefined || v === ''
    ? null
    : Number(v).toLocaleString('sr-RS', { maximumFractionDigits: 2 });

// "TYPE_02_POINTS" → forms:ozCalcType02 (the label the OZ profiles table uses).
const calcTypeKey = (value) => {
  const m = /^TYPE_(\d{2})/.exec(value || '');
  return m ? `ozCalcType${m[1]}` : null;
};

function Row({ label, children }) {
  return (
    <div className="flex items-center gap-2.5 border-b border-zinc-100 py-2 text-sm">
      <span className="w-[150px] shrink-0 truncate whitespace-nowrap text-[13px] text-zinc-500">
        {label}
      </span>
      <span className="min-w-0 flex-1 font-semibold">{children ?? '—'}</span>
    </div>
  );
}

/**
 * Read-only payroll (OZ) profile of one employee, from the company's OZ
 * profiles. "Edit" opens the OZ page on the profiles tab focused on this
 * employee. Admin roles only (the caller wraps it in OZ.VIEW).
 */
export default function OzProfileModal({
  open,
  onClose,
  employeeId,
  companyId,
  fullName,
}) {
  const { t } = useTranslation(['translations', 'forms']);
  const tp = (key, opts) =>
    t(`employeeProfile.${key}`, { ns: 'translations', ...opts });

  const { data: profiles, isLoading } = useEmployeeProfiles(
    open ? companyId : null
  );
  const profile = (profiles || []).find(
    (p) => String(p.employee) === String(employeeId)
  );

  const editUrl = `${ROUTES.admin}/${ROUTES.oz}?company=${companyId}&tab=profiles&employee=${employeeId}`;
  const typeKey = calcTypeKey(profile?.calcType);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={760}
      closable={false}
      classNames={{ content: '!p-0 overflow-hidden !rounded-[18px]' }}
      destroyOnHidden
    >
      <div className="flex items-center justify-between gap-3 bg-[#17242A] px-5 py-4 text-white">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-lg font-extrabold">
              {tp('ozProfile')} — {fullName}
            </span>
            <span className="rounded-full bg-[#FFCC00] px-2.5 py-0.5 text-xs font-semibold text-zinc-900">
              {tp('ozAdminOnly')}
            </span>
          </div>
          <div className="mt-0.5 text-[13px] opacity-85">
            {tp('ozSubtitle')}
          </div>
        </div>
        <button
          type="button"
          aria-label={tp('close')}
          onClick={onClose}
          className="h-8 w-8 cursor-pointer rounded-full border-none bg-white/20 text-white"
        >
          ×
        </button>
      </div>

      {isLoading ? (
        <ModalRowsSkeleton rows={9} />
      ) : !profile ? (
        <div className="px-5 py-6 text-sm text-zinc-500">
          {tp('ozNoProfile')}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-x-6 px-5 py-4">
          <Row label={tp('ozCalcType')}>
            {typeKey ? t(typeKey, { ns: 'forms' }) : profile.calcType}
          </Row>
          <Row label={tp('ozPoints')}>{profile.points}</Row>
          <Row label={tp('ozFixedGross')}>{money(profile.fixedGross)}</Row>
          <Row label={tp('ozFixedNet')}>{money(profile.fixedNet)}</Row>
          <Row label={tp('ozEarningCode')}>{profile.earningCode}</Row>
          <Row label={tp('ozSectorCode')}>{profile.sectorCode}</Row>
          <Row label={tp('ozWorkTime')}>
            {profile.workTimePercent != null
              ? `${profile.workTimePercent}%`
              : null}
          </Row>
          <Row label={tp('ozSeniority')}>
            {profile.seniority?.years != null ? profile.seniority.years : null}
          </Row>
          <Row label={tp('ozContractAvg')}>
            {money(profile.contractAvgGross)}
          </Row>
        </div>
      )}

      <div className="flex items-center gap-1.5 whitespace-nowrap px-5 pt-3 text-xs text-zinc-500">
        <InfoCircleOutlined />
        <span className="truncate">{tp('infoOz')}</span>
      </div>
      <div className="flex justify-end gap-2 px-5 pb-5 pt-3">
        <Button className="btn-default btn-default--hover" onClick={onClose}>
          {tp('close')}
        </Button>
        <Link to={editUrl}>
          <Button type="primary" className="btn-primary">
            {tp('ozEdit')} →
          </Button>
        </Link>
      </div>
    </Modal>
  );
}
